import { Fragment, useEffect, useRef, useState } from 'react'
import { useParams, useNavigate, useSearchParams } from 'react-router-dom'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import hotToast from 'react-hot-toast'
import { conversationSocket } from '@/services/conversationSocket'
import { useAuthStore } from '@/store/authStore'
import {
  ArrowLeft,
  AlertCircle,
  X as CloseIcon,
  MessageSquare,
  StickyNote,
  Bot,
  Trash2,
  Loader2,
} from 'lucide-react'
import { Button } from '@/components/ui/button'
import {
  conversationsApi,
  SendMessageRequest,
  HitlResumeRequest,
  EscalateConversationRequest,
} from '@/services/conversationsApi'
import type { InternalNote } from '@/services/conversationsApi'
import type { CannedVariables } from '@/services/cannedResponsesApi'
import { getWahaInstanceStatus, restartWahaInstance } from '@/services/wahaApi'
import { getChannelInstanceStatus, reconnectChannelInstance } from '@/services/channelsApi'
import { useActiveConversation, useConversationsActions, useConversationsStore } from '@/store/conversationsStore'
import { MessageComposer } from '@/components/chat/MessageComposer'
import Modal from '@/components/ui/modal'
import { teamsApi } from '@/services/teamsApi'
import { defineAbilityFor } from '@/lib/ability'
import { MessageBubble } from '@/components/chat/MessageBubble'
import { ScrollToBottom } from '@/components/chat/ScrollToBottom'
import { TypingIndicator } from '@/components/chat/TypingIndicator'
import { PullToRefreshIndicator } from '@/components/chat/PullToRefreshIndicator'
import { useScrollToBottom } from '@/hooks/useScrollToBottom'
import { usePullToRefresh } from '@/hooks/usePullToRefresh'
import { InternalNotes } from '@/components/chat/InternalNotes'
import { ContactPanel } from '@/components/chat/ContactPanel'
import { ConversationHeader } from '@/components/chat/ConversationHeader'
import { HitlResolutionCard } from '@/components/chat/HitlResolutionCard'
import { motion } from 'framer-motion'
import { StaffConsultationPanel } from '@/components/chat/StaffConsultationPanel'
import { SessionStatusBanner } from '@/components/chat/SessionStatusBanner'
import { BotTakeoverBar } from '@/components/chat/BotTakeoverBar'
import { ClosedConversationBar } from '@/components/chat/ClosedConversationBar'
import { ActiveSessionBanner } from '@/components/chat/ActiveSessionBanner'
import { SessionDemarcationBar } from '@/components/chat/SessionDemarcationBar'
import { TeamQueueTakeoverBar } from '@/components/chat/TeamQueueTakeoverBar'
import { FunnelStageStepper } from '@/components/chat/FunnelStageStepper'
import { ActiveOrderDraftCard } from '@/components/chat/ActiveOrderDraftCard'
import { BotSummaryCard } from '@/components/chat/BotSummaryCard'
import { HandoffCommercialCard } from '@/components/chat/HandoffCommercialCard'
import { BotSummaryHandoffMessage } from '@/components/chat/BotSummaryHandoffMessage'
import { AppointmentRequestCard } from '@/components/chat/AppointmentRequestCard'
import { CloseConversationModal } from '@/components/chat/CloseConversationModal'
import { InlineSuggestedResponse } from '@/components/chat/InlineSuggestedResponse'
import { decideIncomingMessageScroll, isFirstInMessageGroup } from './conversationUx'
import { buildCreateOrderUrlFromConversation } from '@/pages/orders/ordersUx'

const toast = {
  success: (title: string, options?: { description?: string }) => {
    hotToast.success(options?.description ? `${title}: ${options.description}` : title)
  },
  error: (title: string, options?: { description?: string }) => {
    hotToast.error(options?.description ? `${title}: ${options.description}` : title)
  },
}

export default function ConversationView() {
  const { id } = useParams<{ id: string }>()
  const navigate = useNavigate()
  const [searchParams, setSearchParams] = useSearchParams()
  const isClaimedParam = searchParams.get('claimed') === 'true'
  const isReadonlyParam = searchParams.get('readonly') === 'true'
  const queryClient = useQueryClient()
  const messagesEndRef = useRef<HTMLDivElement>(null)
  const messagesContainerRef = useRef<HTMLDivElement>(null)
  const [composerFocusTrigger, setComposerFocusTrigger] = useState(0)
  const lastLoadedIdRef = useRef<string | null>(null)

  const prevMessageCountRef = useRef<{ id: string | undefined; count: number } | null>(null)
  const [newMessagesBelow, setNewMessagesBelow] = useState(0)
  const [sendError, setSendError] = useState<{ message: string; payload: SendMessageRequest } | null>(null)
  const [isCloseModalOpen, setIsCloseModalOpen] = useState(false)
  const [isClearModalOpen, setIsClearModalOpen] = useState(false)

  // Scroll management
  const { showButton: showScrollButton, isAtBottom, scrollToBottom } = useScrollToBottom(messagesContainerRef, {
    threshold: 100,
  })

  // Pull to refresh
  const pullToRefresh = usePullToRefresh(messagesContainerRef, {
    onRefresh: async () => {
      await queryClient.invalidateQueries({ queryKey: ['conversation', id] })
    },
    threshold: 80,
    enabled: true,
  })

  // Reply state (para futuro uso)
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const [_replyingTo, setReplyingTo] = useState<any>(null)

  const { conversation, isLoading, error } = useActiveConversation()
  const { setActiveConversation, setConversationLoading, setConversationError, updateConversation } = useConversationsActions()
  const { user, tenant } = useAuthStore()
  const userRole = user?.role || 'agent'
  const ability = defineAbilityFor(userRole)
  const canViewAllConversations = ability.can('view', 'AllConversations')

  const [mediaLoadState, setMediaLoadState] = useState<Record<string, { loading: boolean; error: boolean }>>({})
  const [lightboxImage, setLightboxImage] = useState<{ url: string; alt?: string } | null>(null)
  const [sessionStatus, setSessionStatus] = useState<string | null>(null)
  const [sessionError, setSessionError] = useState<string | null>(null)
  const [hitlAction, setHitlAction] = useState<'approve' | 'edit' | 'deny'>('approve')
  const [hitlMessage, setHitlMessage] = useState('')
  const [hitlReason, setHitlReason] = useState('')
  const [hitlFeedback, setHitlFeedback] = useState<string | null>(null)
  const [hitlError, setHitlError] = useState<string | null>(null)
  const [showHitlModal, setShowHitlModal] = useState(false)
  const [showResumeBotModal, setShowResumeBotModal] = useState(false)
  const [resumeBotNote, setResumeBotNote] = useState('')
  const [composerPrefillText, setComposerPrefillText] = useState<string | null>(null)
  const [composerPrefillVersion, setComposerPrefillVersion] = useState(0)
  const [hitlPendingResponse, setHitlPendingResponse] = useState<string | null>(null)
  const [dismissedSuggestedDraft, setDismissedSuggestedDraft] = useState<string | null>(null)
  const [otherUserTyping, setOtherUserTyping] = useState<string | null>(null)
  const [isBotTyping, setIsBotTyping] = useState(false)
  const botTypingTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null)
  const reactionThrottleRef = useRef<Map<string, number>>(new Map())
  const [activeTab, setActiveTab] = useState<'messages' | 'notes'>('messages')
  const [showContactPanel, setShowContactPanel] = useState(false)
  const [isSocketConnected, setIsSocketConnected] = useState(true)
  const [visibleMessagesCount, setVisibleMessagesCount] = useState(60)

  const { data: teamsData } = useQuery({
    queryKey: ['teams'],
    queryFn: () => teamsApi.getTeams(),
    enabled: canViewAllConversations,
    staleTime: 60000,
  })

  // Query para obtener la conversación (revalidación ágil para mantener actualizado salesContext/embudo)
  const { data, isLoading: queryLoading, error: queryError } = useQuery({
    queryKey: ['conversation', id],
    queryFn: () => conversationsApi.getConversationById(id!),
    enabled: !!id,
    staleTime: 5000,
    refetchInterval: 10000,
  })

  const { data: hitlStatus } = useQuery({
    queryKey: ['conversation', id, 'hitl-status'],
    queryFn: () => conversationsApi.getHitlStatus(id!),
    enabled: !!id && data?.status !== 'closed',
    refetchInterval: 15000,
  })

  const { data: notes = [] } = useQuery({
    queryKey: ['notes', id],
    queryFn: () => conversationsApi.getNotes(id!),
    enabled: !!id,
    staleTime: 15000,
  })

  // Marcar conversación como leída cuando se abre (o cambia el id)
  useEffect(() => {
    if (!id) return

    // Actualizar optimísticamente el store y la caché de React Query
    const { updateConversation } = useConversationsStore.getState()
    updateConversation(id, { unreadCount: 0 })

    queryClient.setQueriesData({ queryKey: ['conversations'] }, (old: any) => {
      if (!old?.conversations) return old
      return {
        ...old,
        conversations: old.conversations.map((c: any) =>
          c.id === id ? { ...c, unreadCount: 0 } : c
        ),
      }
    })

    conversationsApi
      .markConversationAsRead(id)
      .then(() => {
        // Una vez confirmado que el backend persistió lastAgentReadAt, sincronizar contadores y lista
        queryClient.invalidateQueries({ queryKey: ['conversations', 'counts'] })
        queryClient.invalidateQueries({ queryKey: ['conversations'] })
      })
      .catch((err) => {
        console.error('[ConversationView] Failed to mark conversation as read', err)
      })
  }, [id, queryClient])

  // WebSocket unificado para actualizaciones en tiempo real y reconciliación al reconectar
  useEffect(() => {
    if (!id) return

    const unsubscribe = conversationSocket.subscribe({
      onStatusChange: (connected) => {
        setIsSocketConnected(connected)
      },
      onReconnect: () => {
        // Al reconectar el socket, revalidar para obtener cualquier mensaje entrante durante la interrupción
        queryClient.invalidateQueries({ queryKey: ['conversation', id] })
      },
      onConversationUpdated: (event) => {
        if (event.conversationId !== id) return
        queryClient.setQueryData(['conversation', id], (oldData: any) => {
          if (!oldData) return oldData
          return {
            ...oldData,
            ...(event.mode ? { mode: event.mode } : {}),
            ...(event.status ? { status: event.status } : {}),
            ...(event.assignedTeamId !== undefined ? { assignedTeamId: event.assignedTeamId } : {}),
            ...(event.assignedUserId !== undefined ? { assignedUserId: event.assignedUserId } : {}),
          }
        })
        queryClient.invalidateQueries({ queryKey: ['conversation', id] })
        queryClient.invalidateQueries({ queryKey: ['conversations'] })
      },
      onChatEvent: (event) => {
        // Solo actualizar si es de esta conversación
        if (event.conversationId !== id) {
          // D) Awareness por contacto: si el cliente re-enganchó tras un cierre,
          // su mensaje llega con otro conversationId pero del mismo contacto.
          // Actualizar activeConversationId en la caché de la conversación cerrada
          // para que el banner informativo ("Ir a la conversación activa") esté disponible,
          // pero SIN secuestrar ni navegar forzosamente la pantalla del agente.
          const isInboundMessage = event.type === 'message' || event.type === 'message.media'
          if (isInboundMessage && !event.data?.fromMe) {
            const contactDigits = (value?: string | null) => (value ?? '').replace(/\D+/g, '')
            const eventContact = contactDigits(event.data?.from)
            const currentData: any = queryClient.getQueryData(['conversation', id])
            const currentContact = contactDigits(currentData?.waContact)
            if (eventContact && currentContact && eventContact === currentContact) {
              queryClient.invalidateQueries({ queryKey: ['conversations'] })
              queryClient.setQueryData(['conversation', id], (oldData: any) => {
                if (!oldData) return oldData
                return {
                  ...oldData,
                  activeConversationId: event.conversationId,
                }
              })
              queryClient.invalidateQueries({ queryKey: ['conversation', id] })
            }
          }
          return
        }

        queryClient.setQueryData(['conversation', id], (oldData: any) => {
          if (!oldData) return oldData

          const messages = oldData.messages || []

          switch (event.type) {
            case 'message':
            case 'message.media': {
              // Verificar si el mensaje ya existe (fue enviado desde nuestra app)
              const existingMessageIndex = messages.findIndex((m: any) =>
                m.messageId === event.messageId ||
                (m.messageId?.startsWith('temp_') && m.content === event.data?.text && m.direction === 'out')
              )

              if (existingMessageIndex !== -1) {
                // El mensaje ya existe, solo actualizar su status/ack
                return {
                  ...oldData,
                  messages: messages.map((m: any, index: number) =>
                    index === existingMessageIndex
                      ? {
                          ...m,
                          id: event.messageId,
                          messageId: event.messageId,
                          status: event.data?.status || m.status,
                          ack: event.data?.ack || m.ack,
                          timestamp: new Date(event.timestamp),
                        }
                      : m
                  ),
                }
              }

              // El mensaje NO existe, agregarlo
              let messageType: any = 'text'
              if (event.data?.hasMedia && event.data?.mediaMimetype) {
                const mime = event.data.mediaMimetype
                if (mime.startsWith('image/')) messageType = 'image'
                else if (mime.startsWith('audio/')) messageType = 'audio'
                else if (mime.startsWith('video/')) messageType = 'video'
                else messageType = 'document'
              }

              const newMessage = {
                id: event.messageId,
                messageId: event.messageId,
                content: event.data?.text || '',
                direction: event.data?.fromMe ? 'out' : 'in',
                waFrom: event.data?.from,
                waTo: event.data?.to,
                timestamp: new Date(event.timestamp),
                status: event.data?.status,
                ack: event.data?.ack,
                type: messageType,
                hasMedia: !!event.data?.hasMedia,
                mediaUrl: event.data?.mediaUrl,
                mediaMimetype: event.data?.mediaMimetype,
                mediaFilename: event.data?.mediaFilename,
                source: event.data?.source,
              }

              return {
                ...oldData,
                messages: [...messages, newMessage],
                lastMessage: event.data?.text,
                lastMessageAt: event.timestamp,
              }
            }

            case 'message.ack':
              return {
                ...oldData,
                messages: messages.map((m: any) =>
                  m.messageId === event.messageId
                    ? { ...m, status: event.data?.status, ack: event.data?.ack }
                    : m
                ),
              }

            case 'messages.cleared':
              return {
                ...oldData,
                messages: [],
                previousConversations: [],
                lastMessage: null,
                lastMessageAt: null,
              }

            case 'message.revoked':
              return {
                ...oldData,
                messages: messages.map((m: any) =>
                  m.messageId === event.messageId
                    ? { ...m, content: 'Mensaje eliminado', isRevoked: true }
                    : m
                ),
              }

            case 'message.reaction': {
              const targetMessageId = event.data?.reaction?.messageId || event.messageId
              const emoji = event.data?.reaction?.text || ''
              const from = event.data?.from || ''
              const contactDigits = (conversation?.waContact || oldData?.waContact || '').replace(/\D+/g, '')
              const fromDigits = from.replace(/@.*/, '').replace(/\D+/g, '')
              const isCustomer = Boolean(contactDigits && fromDigits === contactDigits)

              return {
                ...oldData,
                messages: messages.map((m: any) => {
                  if (m.messageId !== targetMessageId && m.id !== targetMessageId) {
                    return m
                  }

                  const currentReactions: any[] = m.reactions || []
                  // Filtrar la reacción previa de este emisor (cliente vs agente)
                  const remainingReactions = currentReactions.filter((r: any) => {
                    const rDigits = (r.from || '').replace(/@.*/, '').replace(/\D+/g, '')
                    if (isCustomer) {
                      return rDigits !== contactDigits
                    } else {
                      return Boolean(contactDigits && rDigits === contactDigits)
                    }
                  })

                  if (!emoji) {
                    return {
                      ...m,
                      reactions: remainingReactions,
                    }
                  }

                  return {
                    ...m,
                    reactions: [
                      ...remainingReactions,
                      {
                        id: `reaction-${Date.now()}`,
                        emoji,
                        from: from || (isCustomer ? conversation?.waContact : 'agent@c.us'),
                        fromLabel: isCustomer ? (conversation?.contactName || conversation?.waContact) : 'Tú',
                      },
                    ],
                  }
                }),
              }
            }

            default:
              return oldData
          }
        })

        if (event.type === 'message' || event.type === 'message.media') {
          if (event.data?.fromMe) {
            setIsBotTyping(false)
            if (botTypingTimeoutRef.current) {
              clearTimeout(botTypingTimeoutRef.current)
              botTypingTimeoutRef.current = null
            }
          } else {
            const currentData: any = queryClient.getQueryData(['conversation', id])
            const currentMode = currentData?.mode || conversation?.mode || data?.mode || 'bot'
            if (currentMode === 'bot') {
              setIsBotTyping(true)
              if (botTypingTimeoutRef.current) clearTimeout(botTypingTimeoutRef.current)
              botTypingTimeoutRef.current = setTimeout(() => setIsBotTyping(false), 25000)
            }
          }
          // Revalidación inmediata; la actualización del bot o cambio de modo se sincroniza deterministamente por onConversationUpdated
          queryClient.invalidateQueries({ queryKey: ['conversation', id] })
        }
      },
      onMessageAck: (data) => {
        if (data.conversationId !== id) return

        queryClient.setQueryData(['conversation', id], (oldData: any) => {
          if (!oldData) return oldData
          return {
            ...oldData,
            messages: (oldData.messages || []).map((m: any) =>
              m.messageId === data.messageId
                ? { ...m, status: data.status, ack: data.ack }
                : m
            ),
          }
        })
      },
      onTyping: (data) => {
        if (data.conversationId === id) {
          const isBot =
            data.userId === 'bot' ||
            data.userName?.toLowerCase() === 'bot' ||
            data.userName?.toLowerCase().includes('asistente')

          if (isBot) {
            setIsBotTyping(Boolean(data.isTyping))
            if (data.isTyping) {
              if (botTypingTimeoutRef.current) clearTimeout(botTypingTimeoutRef.current)
              botTypingTimeoutRef.current = setTimeout(() => {
                setIsBotTyping(false)
              }, 25000)
            } else if (botTypingTimeoutRef.current) {
              clearTimeout(botTypingTimeoutRef.current)
              botTypingTimeoutRef.current = null
            }
          } else {
            setOtherUserTyping(data.isTyping ? data.userName || 'Usuario' : null)
          }
        }
      },
    })

    return () => {
      unsubscribe()
      if (botTypingTimeoutRef.current) {
        clearTimeout(botTypingTimeoutRef.current)
        botTypingTimeoutRef.current = null
      }
    }
  }, [id, queryClient, conversation?.mode, data?.mode])

  // Scroll suave al activarse el indicador de escritura del bot
  useEffect(() => {
    if (isBotTyping && isAtBottom) {
      scrollToBottom('smooth')
    }
  }, [isBotTyping, isAtBottom, scrollToBottom])

  // Mutación para enviar mensaje con actualización optimista
  const sendMessageMutation = useMutation({
    mutationFn: (messageData: SendMessageRequest) =>
      conversationsApi.sendMessage(id!, messageData),
    onMutate: async (messageData) => {
      await queryClient.cancelQueries({ queryKey: ['conversation', id] })

      const previousData = queryClient.getQueryData(['conversation', id])

      const tempId = `temp_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`
      queryClient.setQueryData(['conversation', id], (oldData: any) => {
        if (!oldData) return oldData

        const mediaData = messageData.media?.data
        const optimisticMediaUrl =
          messageData.media?.url ||
          (mediaData
            ? mediaData.startsWith('data:')
              ? mediaData
              : `data:${messageData.media?.mimetype || 'application/octet-stream'};base64,${mediaData}`
            : undefined)

        const newMessage = {
          id: tempId,
          messageId: tempId,
          content: messageData.content || '',
          direction: 'out',
          waFrom: oldData.waInstance?.profileName || 'Agente',
          waTo: oldData.waContact,
          timestamp: new Date(),
          status: 'pending',
          type: messageData.type || 'text',
          hasMedia: !!messageData.media,
          mediaUrl: optimisticMediaUrl,
          mediaMimetype: messageData.media?.mimetype,
          mediaFilename: messageData.media?.filename,
        }

        return {
          ...oldData,
          messages: [...(oldData.messages || []), newMessage],
          lastMessage: messageData.content,
          lastMessageAt: new Date(),
        }
      })

      return { previousData, tempId }
    },
    onSuccess: (response, _messageData, context) => {
      queryClient.setQueryData(['conversation', id], (oldData: any) => {
        if (!oldData) return oldData

        return {
          ...oldData,
          messages: oldData.messages.map((msg: any) =>
            msg.messageId === context?.tempId
              ? {
                  ...msg,
                  id: response.messageId,
                  messageId: response.messageId,
                  status: 'sent',
                  timestamp: new Date(response.timestamp),
                }
              : msg
          ),
        }
      })

      setSendError(null)
      queryClient.invalidateQueries({ queryKey: ['conversations'] })
    },
    onError: (error, messageData, context) => {
      console.error('Error sending message:', error)

      if (context?.previousData) {
        queryClient.setQueryData(['conversation', id], context.previousData)
      }

      setSendError({
        message:
          (error as any)?.response?.data?.error ||
          (error instanceof Error ? error.message : '') ||
          'No se pudo enviar el mensaje.',
        payload: messageData,
      })
    },
  })

  // Mutación para claim (tomar caso desde la vista de conversación)
  const claimMutation = useMutation({
    mutationFn: (conversationId: string) => conversationsApi.claimConversation(conversationId),
    onSuccess: (updatedConv, conversationId) => {
      toast.success('Chat tomado exitosamente', {
        description: 'Ahora puedes responder a este cliente',
      })
      if (user?.id) {
        updateConversation(conversationId, {
          assignedUserId: user.id,
          status: 'open' as any,
        })
      }
      // Actualizar la caché de TanStack Query INMEDIATAMENTE para sincronizar la vista y ocultar TeamQueueTakeoverBar
      queryClient.setQueryData(['conversation', conversationId], (oldData: any) => {
        if (!oldData) return updatedConv
        return {
          ...oldData,
          ...(updatedConv || {}),
          assignedUserId: user?.id || updatedConv?.assignedUserId || oldData.assignedUserId,
          assignedUser: (updatedConv as any)?.assignedUser || (user ? { ...user } : oldData.assignedUser),
          status: 'open',
        }
      })
      queryClient.invalidateQueries({ queryKey: ['conversation', conversationId] })
      queryClient.invalidateQueries({ queryKey: ['conversations'] })
      navigate(`/app/conversations/${conversationId}?claimed=true`, { replace: true })
    },
    onError: (err: any) => {
      if (err.response?.status === 409) {
        toast.error('Chat ya asignado', {
          description: err.response?.data?.message || 'Otro agente tomó este chat antes que tú',
        })
      } else if (err.response?.status === 403) {
        toast.error('Sin permisos', {
          description: err.response?.data?.message || 'No tienes permiso para tomar este chat',
        })
      } else {
        toast.error('Error al tomar el chat', {
          description: err.response?.data?.message || err.response?.data?.error || 'Intenta nuevamente',
        })
      }
    },
  })

  // Mutación para escalar conversación
  const escalateMutation = useMutation({
    mutationFn: (data: 'bot' | 'human' | EscalateConversationRequest) => {
      const payload: EscalateConversationRequest = typeof data === 'string' ? { mode: data } : data
      return conversationsApi.escalateConversation(id!, payload)
    },
    onSuccess: (_data, variables) => {
      queryClient.invalidateQueries({ queryKey: ['conversation', id] })
      queryClient.invalidateQueries({ queryKey: ['conversations'] })
      const mode = typeof variables === 'string' ? variables : variables.mode
      if (mode === 'human') {
        requestAnimationFrame(() => {
          scrollToBottom('smooth')
          setComposerFocusTrigger((t) => t + 1)
        })
      } else {
        toast.success('Bot de ventas reanudado')
      }
    },
    onError: (err: any) => {
      toast.error('Error al cambiar modo de atención', {
        description: err.response?.data?.error || 'Intenta nuevamente',
      })
    },
  })

  const handleOpenResumeBotModal = () => {
    setResumeBotNote('')
    setShowResumeBotModal(true)
  }

  const handleConfirmResumeBot = () => {
    const note = resumeBotNote.trim()
    escalateMutation.mutate({
      mode: 'bot',
      reason: note || undefined,
      hitlResolution: note
        ? { action: 'approve', notes: note, summary: note }
        : { action: 'approve' },
    })
    setShowResumeBotModal(false)
    setResumeBotNote('')
  }

  // Mutación para cambiar status
  const statusMutation = useMutation({
    mutationFn: (data: { status: 'open' | 'closed'; closeReason?: string; closeNotes?: string } | 'open' | 'closed') => {
      const payload = typeof data === 'string' ? { status: data } : data
      return conversationsApi.updateConversationStatus(id!, payload)
    },
    onSuccess: () => {
      setIsCloseModalOpen(false)
      queryClient.invalidateQueries({ queryKey: ['conversation', id] })
      queryClient.invalidateQueries({ queryKey: ['conversations'] })
    },
  })

  const hitlResumeMutation = useMutation({
    mutationFn: (payload: HitlResumeRequest) =>
      conversationsApi.resumeHitl(id!, payload),
    onSuccess: (result) => {
      queryClient.invalidateQueries({ queryKey: ['conversation', id, 'hitl-status'] })
      queryClient.invalidateQueries({ queryKey: ['conversation', id] })
      setHitlError(null)

      if (result.success && result.messageSent) {
        setHitlPendingResponse(null)
        setComposerPrefillText(null)
        setHitlFeedback('Decision aplicada y mensaje enviado al cliente.')
      } else if (result.pendingResponse && result.success) {
        setHitlPendingResponse(result.pendingResponse)
        setComposerPrefillText(result.pendingResponse)
        setComposerPrefillVersion((value) => value + 1)
        setHitlFeedback(
          result.deliveryError
            ? 'Decision aplicada, pero no se pudo enviar automaticamente. Mensaje cargado en el composer para revision final.'
            : result.hitlPaused
              ? 'El flujo sigue en pausa HITL.'
              : 'HITL resumido correctamente. Mensaje autocompletado en el composer para revision.'
        )
      } else {
        setHitlFeedback(
          result.success
            ? result.hitlPaused
              ? 'El flujo sigue en pausa HITL.'
              : 'HITL resumido correctamente.'
            : `No se pudo resumir: ${result.error || 'error desconocido'}`
        )
      }

      if (!result.hitlPaused) {
        setShowHitlModal(false)
      }
    },
    onError: () => {
      setHitlError('No se pudo enviar el resume HITL')
    },
  })

  // Mutación para eliminar mensaje
  const deleteMessageMutation = useMutation({
    mutationFn: (messageId: string) => conversationsApi.deleteMessage(messageId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['conversation', id] })
    },
  })

  // Mutación para vaciar todos los mensajes de la conversación
  const clearMessagesMutation = useMutation({
    mutationFn: () => {
      if (!id) throw new Error('ID de conversación no disponible')
      return conversationsApi.clearConversationMessages(id)
    },
    onSuccess: () => {
      toast.success('Mensajes vaciados exitosamente')
      setIsClearModalOpen(false)
      queryClient.setQueryData(['conversation', id], (oldData: any) => {
        if (!oldData) return oldData
        return {
          ...oldData,
          messages: [],
          previousConversations: [],
          lastMessage: null,
          lastMessageAt: null,
        }
      })
      queryClient.invalidateQueries({ queryKey: ['conversation', id] })
      queryClient.invalidateQueries({ queryKey: ['conversations'] })
    },
    onError: (error: any) => {
      toast.error('Error al vaciar los mensajes', {
        description: error?.response?.data?.error || error.message,
      })
    },
  })

  // Mutación para cancelar mensaje programado
  const cancelScheduledMessageMutation = useMutation({
    mutationFn: (messageId: string) => conversationsApi.cancelScheduledMessage(messageId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['conversation', id] })
    },
  })

  // Mutación para agregar/quitar reacción con actualización optimista y rollback
  const addReactionMutation = useMutation({
    mutationFn: ({ messageId, reaction }: { messageId: string; reaction: string }) =>
      conversationsApi.addReaction(messageId, reaction),
    onMutate: async ({ messageId, reaction }) => {
      await queryClient.cancelQueries({ queryKey: ['conversation', id] })
      const previousData = queryClient.getQueryData(['conversation', id])

      queryClient.setQueryData(['conversation', id], (oldData: any) => {
        if (!oldData?.messages) return oldData
        const contactDigits = (conversation?.waContact || oldData?.waContact || '').replace(/\D+/g, '')

        return {
          ...oldData,
          messages: oldData.messages.map((m: any) => {
            if (m.messageId !== messageId && m.id !== messageId) return m

            const currentReactions: any[] = m.reactions || []
            // Remover la reacción previa del agente (la que no sea del cliente)
            const remainingReactions = currentReactions.filter((r: any) => {
              const rDigits = (r.from || '').replace(/@.*/, '').replace(/\D+/g, '')
              return Boolean(contactDigits && rDigits === contactDigits)
            })

            const updatedReactions = reaction
              ? [
                  ...remainingReactions,
                  {
                    id: `temp-${Date.now()}`,
                    emoji: reaction,
                    from: 'agent@c.us',
                    fromLabel: 'Tú',
                  },
                ]
              : remainingReactions

            return {
              ...m,
              reactions: updatedReactions,
            }
          }),
        }
      })

      return { previousData }
    },
    onError: (_err, _variables, context) => {
      if (context?.previousData) {
        queryClient.setQueryData(['conversation', id], context.previousData)
      }
      toast.error('No se pudo actualizar la reacción')
    },
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: ['conversation', id] })
    },
  })

  // Mutación para transferir conversación a otro equipo
  const transferMutation = useMutation({
    mutationFn: async (teamId: string) => {
      const shouldEscalate = conversation?.mode === 'bot'

      if (conversation && shouldEscalate) {
        await conversationsApi.escalateConversation(conversation.id, { mode: 'human' })
      }

      return conversationsApi.transferConversation(id!, teamId)
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['conversation', id] })
      queryClient.invalidateQueries({ queryKey: ['conversations'] })
    },
  })

  useEffect(() => {
    setHitlAction('approve')
    setHitlMessage('')
    setHitlReason('')
    setHitlFeedback(null)
    setHitlError(null)
    setShowHitlModal(false)
    setComposerPrefillText(null)
    setComposerPrefillVersion(0)
    setHitlPendingResponse(null)
    setSendError(null)
  }, [id])

  const handleResumeHitl = () => {
    if (!id) return

    const editedMessage = hitlMessage.trim()
    const reason = hitlReason.trim()
    if (hitlAction === 'edit' && !editedMessage) {
      setHitlError('Debes escribir el mensaje editado para la accion edit.')
      setHitlFeedback(null)
      return
    }

    setHitlError(null)
    setHitlFeedback(null)
    hitlResumeMutation.mutate({
      action: hitlAction,
      ...(hitlAction === 'edit' && editedMessage ? { message: editedMessage } : {}),
      ...(reason ? { reason } : {}),
    })
  }

  const loadPendingResponseAsDraft = (value: string | null) => {
    const normalized = typeof value === 'string' ? value.trim() : ''
    if (!normalized) return
    setComposerPrefillText(normalized)
    setComposerPrefillVersion((current) => current + 1)
    setHitlFeedback('pendingResponse cargado en el composer para revision final.')
    setHitlError(null)
  }

  // Sincronizar con store
  useEffect(() => {
    setConversationLoading(queryLoading)
  }, [queryLoading, setConversationLoading])

  useEffect(() => {
    if (queryError) {
      setConversationError(queryError instanceof Error ? queryError.message : 'Error al cargar conversación')
    } else {
      setConversationError(null)
    }
  }, [queryError, setConversationError])

  useEffect(() => {
    if (data) {
      setActiveConversation(data)
    }
  }, [data, setActiveConversation])


  // Mensajes nuevos: el hook useScrollToBottom ya autoscrollea si el usuario está al fondo.
  // Aquí solo forzamos scroll en mensajes propios y contamos los nuevos si está leyendo historial.
  useEffect(() => {
    const messages = conversation?.messages ?? []
    const decision = decideIncomingMessageScroll({
      previous: prevMessageCountRef.current,
      currentId: id,
      currentCount: messages.length,
      lastDirection: messages[messages.length - 1]?.direction,
      isAtBottom,
    })
    prevMessageCountRef.current = { id, count: messages.length }

    if (decision.action === 'scroll') {
      scrollToBottom('smooth')
    } else if (decision.action === 'count') {
      setNewMessagesBelow((count) => count + decision.added)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [conversation?.messages])

  useEffect(() => {
    if (isAtBottom) setNewMessagesBelow(0)
  }, [isAtBottom])

  useEffect(() => {
    setNewMessagesBelow(0)
    setVisibleMessagesCount(60)
  }, [id])

  // Asegurar que la ventana del navegador nunca quede desplazada hacia abajo al abrir un chat
  useEffect(() => {
    if (typeof window !== 'undefined' && window.scrollY > 0) {
      window.scrollTo(0, 0)
    }
  }, [id])

  // Auto-scroll al último mensaje al cargar o cambiar de conversación sin desplazar la ventana
  useEffect(() => {
    if (id && conversation?.id === id && (conversation.messages?.length ?? 0) > 0) {
      if (lastLoadedIdRef.current !== id) {
        lastLoadedIdRef.current = id
        requestAnimationFrame(() => {
          if (messagesContainerRef.current) {
            messagesContainerRef.current.scrollTop = messagesContainerRef.current.scrollHeight
          }
        })
      }
    }
  }, [id, conversation?.id, conversation?.messages?.length])

  // Cuando la conversación fue tomada recientemente (claimed=true), scroll suave y foco en el input
  useEffect(() => {
    if (isClaimedParam && conversation?.id === id) {
      requestAnimationFrame(() => {
        scrollToBottom('smooth')
        setComposerFocusTrigger((t) => t + 1)
      })
      const nextParams = new URLSearchParams(searchParams)
      nextParams.delete('claimed')
      nextParams.delete('readonly')
      setSearchParams(nextParams, { replace: true })
    }
  }, [isClaimedParam, conversation?.id, id, searchParams, setSearchParams, scrollToBottom])

  // Limpiar conversación activa al desmontar
  useEffect(() => {
    return () => {
      setActiveConversation(null)
    }
  }, [setActiveConversation])

  // Atajo de teclado Esc para cerrar modales y paneles flotantes
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        if (lightboxImage) setLightboxImage(null)
        else if (showContactPanel) setShowContactPanel(false)
        else if (showHitlModal) setShowHitlModal(false)
      }
    }
    window.addEventListener('keydown', handleKeyDown)
    return () => window.removeEventListener('keydown', handleKeyDown)
  }, [lightboxImage, showContactPanel, showHitlModal])

  const [isReconnectingSession, setIsReconnectingSession] = useState(false)

  // Verificar estado de la sesión de manera pasiva (sin reiniciar instancias de fondo en cada click de envío)
  const checkSessionStatus = async (): Promise<boolean> => {
    if (conversation?.channelInstanceId) {
      try {
        const status = await getChannelInstanceStatus(conversation.channelInstanceId)
        setSessionStatus(status.status)

        if (status.status !== 'CONNECTED') {
          setSessionError(
            `La sesión del canal no está activa (Estado: ${status.status}). Reconéctala para enviar mensajes.`
          )
          return false
        }

        setSessionError(null)
        return true
      } catch (error) {
        console.error('Error verificando estado de canal:', error)
        setSessionError('No se pudo verificar el estado de la instancia de canal')
        return false
      }
    }

    if (!conversation?.waInstance?.id) {
      setSessionError('No hay sesión de WhatsApp asociada a esta conversación')
      return false
    }

    try {
      const status = await getWahaInstanceStatus(conversation.waInstance.id)
      setSessionStatus(status.wahaStatus)

      if (status.wahaStatus !== 'WORKING') {
        setSessionError(
          `La sesión de WhatsApp no está sincronizada (Estado: ${status.wahaStatus}). Reconéctala para continuar.`
        )
        return false
      }

      setSessionError(null)
      return true
    } catch (error) {
      console.error('Error verificando estado de WhatsApp:', error)
      setSessionError('No se pudo verificar el estado de la sesión de WhatsApp')
      return false
    }
  }

  const handleReconnectSession = async () => {
    setIsReconnectingSession(true)
    try {
      if (conversation?.channelInstanceId) {
        await reconnectChannelInstance(conversation.channelInstanceId)
        const status = await getChannelInstanceStatus(conversation.channelInstanceId)
        setSessionStatus(status.status)
        if (status.status === 'CONNECTED') {
          setSessionError(null)
        }
      } else if (conversation?.waInstance?.id) {
        await restartWahaInstance(conversation.waInstance.id)
        const status = await getWahaInstanceStatus(conversation.waInstance.id)
        setSessionStatus(status.wahaStatus)
        if (status.wahaStatus === 'WORKING') {
          setSessionError(null)
        }
      }
    } catch (error) {
      console.error('Error reconectando sesión:', error)
      setSessionError('No se pudo reconectar la sesión. Verifica el código QR o el estado del canal.')
    } finally {
      setIsReconnectingSession(false)
    }
  }

  const salesContext = conversation?.salesContext
  const primaryDraftItem = salesContext?.activeOrderDraft?.items?.[0]
  const productName = salesContext?.lastMentionedProductName || primaryDraftItem?.title || salesContext?.product?.title || ''
  const productPrice = primaryDraftItem?.unitPriceCents
    ? `$${(primaryDraftItem.unitPriceCents / 100).toFixed(2)}`
    : (salesContext?.product?.price ? `$${Number(salesContext.product.price).toFixed(2)}` : '')
  const totalDraft = salesContext?.activeOrderDraft?.totalCents
    ? `$${(salesContext.activeOrderDraft.totalCents / 100).toFixed(2)}`
    : ''
  const fulfillmentAddress = salesContext?.activeOrderDraft?.fulfillmentAddress || ''

  const cannedVarValues: CannedVariables = {
    nombre_cliente: conversation?.contactName || conversation?.waContact,
    nombre_tienda: tenant?.name || 'Nuestra Tienda',
    numero_cliente: conversation?.waContact,
    producto: productName,
    precio: productPrice,
    total: totalDraft || productPrice,
    direccion_envio: fulfillmentAddress,
    metodos_pago: 'Transferencia bancaria, Pago Móvil o Efectivo',
  }

  // Handlers para el MessageComposer
  const handleSendText = async (text: string, scheduledAt?: string) => {
    if (!scheduledAt) {
      const isSessionActive = await checkSessionStatus()
      if (!isSessionActive) {
        return
      }
    }

    await sendMessageMutation.mutateAsync({
      content: text,
      type: 'text',
      scheduledAt,
    })
  }

  const handleSendImage = async (file: File, base64: string, caption?: string) => {
    const isSessionActive = await checkSessionStatus()
    if (!isSessionActive) {
      return
    }

    await sendMessageMutation.mutateAsync({
      content: caption,
      type: 'image',
      media: {
        mimetype: file.type || 'image/jpeg',
        filename: file.name || 'image.jpg',
        data: base64,
      },
    })
  }

  const handleSendFile = async (file: File, base64: string, caption?: string) => {
    const isSessionActive = await checkSessionStatus()
    if (!isSessionActive) {
      return
    }

    await sendMessageMutation.mutateAsync({
      content: caption,
      type: 'document',
      media: {
        mimetype: file.type || 'application/octet-stream',
        filename: file.name || 'archivo',
        data: base64,
      },
    })
  }

  const handleMediaLoad = (messageId: string) => {
    setMediaLoadState((prev) => ({
      ...prev,
      [messageId]: { ...(prev[messageId] || {}), loading: false, error: false },
    }))
  }

  const handleMediaError = (messageId: string) => {
    setMediaLoadState((prev) => ({
      ...prev,
      [messageId]: { ...(prev[messageId] || {}), loading: false, error: true },
    }))
  }

  const handleDeleteMessage = (messageId: string) => {
    deleteMessageMutation.mutate(messageId)
  }

  const handleCancelScheduledMessage = (messageId: string) => {
    cancelScheduledMessageMutation.mutate(messageId)
  }

  const handleAddReaction = (messageId: string, reaction: string) => {
    const now = Date.now()
    const lastCalled = reactionThrottleRef.current.get(messageId) || 0
    if (now - lastCalled < 350) {
      // Ignorar ráfagas o dobles clics accidentales repetidos
      return
    }
    reactionThrottleRef.current.set(messageId, now)
    addReactionMutation.mutate({ messageId, reaction })
  }

  const handleTransferConversation = (teamId: string) => {
    if (!id) return
    transferMutation.mutate(teamId)
  }

  const formatMessageTime = (timestamp: Date | string | number | null | undefined) => {
    if (!timestamp) return ''
    const date = timestamp instanceof Date ? timestamp : new Date(timestamp)
    if (isNaN(date.getTime())) return ''
    return new Intl.DateTimeFormat('es-ES', {
      hour: '2-digit',
      minute: '2-digit',
    }).format(date)
  }

  const formatMessageDate = (timestamp: Date | string | number | null | undefined) => {
    if (!timestamp) return ''
    const messageDate = timestamp instanceof Date ? timestamp : new Date(timestamp)
    if (isNaN(messageDate.getTime())) return ''

    const today = new Date()
    if (messageDate.toDateString() === today.toDateString()) {
      return 'Hoy'
    }

    const yesterday = new Date(today)
    yesterday.setDate(yesterday.getDate() - 1)
    if (messageDate.toDateString() === yesterday.toDateString()) {
      return 'Ayer'
    }

    return messageDate.toLocaleDateString('es-ES', {
      day: '2-digit',
      month: 'short',
      year: 'numeric'
    })
  }

  // B) Historial unificado con demarcación de sesión: sesiones cerradas previas
  // del mismo contacto (asc) + sesión actual. Cada mensaje lleva metadatos de su
  // sesión para que la lista renderice un divisor visual al cambiar de sesión.
  const previousConversations = conversation?.previousConversations ?? []
  const currentMessages = conversation?.messages || []
  const rawMessages: any[] = previousConversations.length
    ? [
        ...previousConversations.flatMap((session) =>
          session.messages.map((message: any) => ({
            ...message,
            sessionId: session.id,
            sessionKind: 'closed' as const,
            sessionClosedAt: session.closedAt,
            sessionCloseReason: session.closeReason,
          }))
        ),
        ...currentMessages.map((message: any) => ({
          ...message,
          sessionId: conversation?.id,
          sessionKind: 'current' as const,
        })),
      ]
    : currentMessages.map((message: any) => ({
        ...message,
        sessionId: conversation?.id,
        sessionKind: 'current' as const,
      }))

  // Integración cronológica del Resumen de la Agente IA en el timeline del chat
  const botSummaryNotes = notes.filter((n) => n.source === 'bot_summary')
  const isSystemEscalation = (m: any) =>
    m.source === 'system' ||
    m.status === 'escalated' ||
    (typeof m.messageId === 'string' &&
      (m.messageId.startsWith('order_confirmed_escalation_') ||
        m.messageId.startsWith('escalation_')))

  const matchedNoteIds = new Set<string>()

  // 1. Identificar y enriquecer mensajes de escalación existentes en el log
  const enrichedMessages = rawMessages.map((msg: any) => {
    if (isSystemEscalation(msg)) {
      const msgTime = new Date(msg.timestamp || msg.createdAt).getTime()
      const matchingNote =
        botSummaryNotes.find((note) => {
          const noteTime = new Date(note.createdAt).getTime()
          return Math.abs(msgTime - noteTime) <= 10 * 60 * 1000
        }) || (botSummaryNotes.length === 1 ? botSummaryNotes[0] : undefined)

      if (matchingNote) {
        matchedNoteIds.add(matchingNote.id)
      }

      return {
        ...msg,
        isAiHandoffSummary: true,
        handoffNote: matchingNote,
        handoffSummaryContent: matchingNote?.content || msg.content || msg.text,
      }
    }
    return msg
  })

  // 2. Para notas bot_summary que no tuvieron un mensaje en log, insertarlas como eventos en el timeline
  const unlinkedBotSummaryItems = botSummaryNotes
    .filter((note) => !matchedNoteIds.has(note.id))
    .map((note) => ({
      id: `bot_summary_note_${note.id}`,
      messageId: `bot_summary_note_${note.id}`,
      isAiHandoffSummary: true,
      handoffNote: note,
      handoffSummaryContent: note.content,
      timestamp: new Date(note.createdAt),
      createdAt: note.createdAt,
      direction: 'out' as const,
      source: 'system',
      status: 'escalated',
      sessionId: conversation?.id,
      sessionKind: 'current' as const,
    }))

  const allMessages: any[] = [...enrichedMessages, ...unlinkedBotSummaryItems].sort(
    (a, b) =>
      new Date(a.timestamp || a.createdAt).getTime() -
      new Date(b.timestamp || b.createdAt).getTime()
  )
  const hasHiddenPreviousMessages = allMessages.length > visibleMessagesCount
  const visibleMessages = hasHiddenPreviousMessages
    ? allMessages.slice(-visibleMessagesCount)
    : allMessages

  // Mensajes donde inicia una sesión distinta a la del mensaje visible previo:
  // ahí la lista renderiza la demarcación de sesión.
  const sessionBreakMessageIds = new Set<string>()
  for (let i = 0; i < visibleMessages.length; i += 1) {
    const msg = visibleMessages[i] as any
    const prev = i > 0 ? (visibleMessages[i - 1] as any) : undefined
    if (!msg.sessionKind) continue
    if (!prev || prev.sessionId !== msg.sessionId) {
      sessionBreakMessageIds.add(msg.id)
    }
  }

  const groupMessagesByDate = () => {
    if (!visibleMessages.length) return []

    const groups: { date: string; messages: typeof allMessages }[] = []
    let currentDate = ''

    visibleMessages.forEach((message) => {
      const messageDate = formatMessageDate(message.timestamp)
      if (messageDate !== currentDate) {
        currentDate = messageDate
        groups.push({ date: messageDate, messages: [] })
      }
      groups[groups.length - 1].messages.push(message)
    })

    return groups
  }

  const getContactAvatar = (waContact: string, contactName?: string) => {
    if (contactName) {
      return contactName.slice(0, 2).toUpperCase()
    }
    const digits = waContact.replace(/\D/g, '').slice(-4)
    return digits.slice(0, 2)
  }

  const getAvatarColor = (waContact: string) => {
    const colors = [
      'bg-blue-500',
      'bg-green-500',
      'bg-purple-500',
      'bg-pink-500',
      'bg-indigo-500',
      'bg-red-500',
      'bg-yellow-500',
      'bg-teal-500',
    ]
    const index = waContact.split('').reduce((acc, char) => acc + char.charCodeAt(0), 0)
    return colors[index % colors.length]
  }

  const getSalesStageBadge = (stage?: string) => {
    switch (stage) {
      case 'discovery':
      case 'greeting':
        return { label: 'Exploración', style: 'bg-blue-50 text-blue-700 border-blue-200' }
      case 'qualification':
        return { label: 'Calificación', style: 'bg-cyan-50 text-cyan-700 border-cyan-200' }
      case 'recommendation':
      case 'product_presentation':
        return { label: 'Presentación', style: 'bg-purple-50 text-purple-700 border-purple-200' }
      case 'price_discussion':
      case 'negotiation':
        return { label: 'Negociación', style: 'bg-amber-50 text-amber-700 border-amber-200' }
      case 'objection_handling':
      case 'objection':
        return { label: 'Objeción', style: 'bg-orange-50 text-orange-700 border-orange-200' }
      case 'purchase_intent':
      case 'closing':
      case 'checkout':
        return { label: 'Cierre / Checkout', style: 'bg-emerald-50 text-emerald-700 border-emerald-200' }
      default:
        return { label: stage || 'En curso', style: 'bg-slate-50 text-slate-700 border-slate-200' }
    }
  }

  const isTeamQueueChat = Boolean(
    conversation &&
    (conversation.status === 'QUEUED' ||
      (isReadonlyParam && !conversation.assignedUserId) ||
      (!conversation.assignedUserId && conversation.status !== 'closed'))
  )
  const canTransferConversation =
    canViewAllConversations && Array.isArray(teamsData) && teamsData.length > 0
  const hitlPending = Boolean(hitlStatus?.pending)
  const hitlReasonFromTrace =
    typeof (hitlStatus?.request as Record<string, unknown> | null)?.['reason'] === 'string'
      ? String((hitlStatus?.request as Record<string, unknown> | null)?.['reason'])
      : null
  const hitlReasonFriendlyLabel = hitlReasonFromTrace
    ? {
        high_purchase_signal: 'Señal de compra alta',
        payment_signal_detected: 'Señal de pago detectada',
      }[hitlReasonFromTrace] || 'Motivo HITL'
    : 'Sin motivo'
  const hitlReasonDisplay = hitlReasonFromTrace || 'Sin motivo'
  const hitlReasonBadge = hitlReasonFromTrace ? (
    <span className="inline-flex items-center rounded-full bg-orange-100 px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wide text-orange-800">
      {hitlReasonFromTrace}
    </span>
  ) : (
    <span className="text-[11px] text-orange-700">Sin motivo</span>
  )
  const hitlPendingPreviewFromTrace =
    typeof (hitlStatus?.request as Record<string, unknown> | null)?.['pendingResponsePreview'] === 'string'
      ? String((hitlStatus?.request as Record<string, unknown> | null)?.['pendingResponsePreview'])
      : null
  const hitlDraftCandidate = (hitlPendingResponse || hitlPendingPreviewFromTrace || '').trim() || null
  const botSummaryNote: InternalNote | null =
    notes.find((note) => note.source === 'bot_summary') || null
  const botSummaryText = (botSummaryNote?.content || '').trim()

  if (isLoading) {
    return (
      <div className="flex flex-col items-center justify-center h-full space-y-4">
        <div className="w-12 h-12 border-4 border-whatsapp-500 border-t-transparent rounded-full animate-spin"></div>
        <p className="text-gray-500">Cargando conversación...</p>
      </div>
    )
  }

  if (error || !conversation) {
    return (
      <div className="flex flex-col items-center justify-center h-full space-y-4 px-4">
        <div className="text-6xl">❌</div>
        <p className="text-red-500 text-center mb-4">{error || 'Conversación no encontrada'}</p>
        <Button
          onClick={() => navigate('/app/conversations')}
          className="bg-whatsapp-500 hover:bg-whatsapp-600"
        >
          <ArrowLeft className="h-4 w-4 mr-2" />
          Volver a Conversaciones
        </Button>
      </div>
    )
  }

  const messageGroups = groupMessagesByDate()

  const handleOpenOrder = () => {
    if (!id) return
    navigate(
      buildCreateOrderUrlFromConversation({
        conversationId: id,
        salesContext,
        contactName: conversation?.contactName,
        customerPhone: conversation?.waContact,
        channelInstanceId: conversation?.channelInstanceId || conversation?.waInstanceId,
        channelType: conversation?.channelType || (conversation?.waContact ? 'WHATSAPP' : undefined),
      })
    )
  }

  return (
    <div className="flex flex-col h-full bg-gray-50 lg:bg-white">
      {/* Header */}
      <ConversationHeader
        conversation={{
          id: conversation.id,
          contactName: conversation.contactName,
          waContact: conversation.waContact,
          avatarUrl: conversation.avatarUrl,
          status: conversation.status,
          mode: conversation.mode,
          channelType: conversation.channelType,
          waInstance: conversation.waInstance,
        }}
        otherUserTyping={otherUserTyping}
        isBotTyping={isBotTyping}
        canTransferConversation={canTransferConversation}
        teams={teamsData as any}
        isStatusPending={statusMutation.isPending}
        isModePending={escalateMutation.isPending}
        isTransferPending={transferMutation.isPending}
        isClearPending={clearMessagesMutation.isPending}
        onBack={() => navigate('/app/conversations')}
        onOpenContact={() => setShowContactPanel(true)}
        onOpenOrder={handleOpenOrder}
        onClearMessages={() => setIsClearModalOpen(true)}
        onToggleMode={(nextMode) => {
          if (nextMode === 'bot') {
            handleOpenResumeBotModal()
          } else {
            escalateMutation.mutate('human')
          }
        }}
        onToggleStatus={(nextStatus) => {
          if (nextStatus === 'closed') {
            setIsCloseModalOpen(true)
          } else {
            statusMutation.mutate({ status: 'open' })
          }
        }}
        onTransfer={handleTransferConversation}
      />

      {/* Tabs */}
      <div className="border-b border-gray-200 bg-white px-2 sm:px-4">
        <div role="tablist" aria-label="Secciones de la conversación" className="flex gap-1">
          <button
            role="tab"
            id="tab-messages"
            aria-selected={activeTab === 'messages'}
            aria-controls="panel-messages"
            onClick={() => setActiveTab('messages')}
            className={`flex items-center gap-2 px-3 py-2.5 text-sm font-medium border-b-2 transition-colors ${
              activeTab === 'messages'
                ? 'border-green-500 text-green-600'
                : 'border-transparent text-gray-500 hover:text-gray-700'
            }`}
          >
            <MessageSquare className="h-4 w-4" />
            <span>Mensajes</span>
          </button>
          <button
            role="tab"
            id="tab-notes"
            aria-selected={activeTab === 'notes'}
            aria-controls="panel-notes"
            onClick={() => setActiveTab('notes')}
            className={`flex items-center gap-2 px-3 py-2.5 text-sm font-medium border-b-2 transition-colors ${
              activeTab === 'notes'
                ? 'border-yellow-500 text-yellow-600'
                : 'border-transparent text-gray-500 hover:text-gray-700'
            }`}
          >
            <StickyNote className="h-4 w-4" />
            <span>Notas</span>
            {notes.length > 0 && (
              <span className="rounded-full bg-amber-100 px-1.5 py-0.2 text-[10px] font-bold text-amber-800">
                {notes.length}
              </span>
            )}
          </button>
        </div>
      </div>

      {/* Content Area */}
      {activeTab === 'notes' ? (
        <div
          role="tabpanel"
          id="panel-notes"
          aria-labelledby="tab-notes"
          className="flex-1 overflow-hidden"
        >
          <InternalNotes conversationId={id!} />
        </div>
      ) : (
      <>
        {!isSocketConnected && (
          <div
            role="status"
            className="bg-amber-500/15 border-b border-amber-300/60 px-4 py-1.5 text-center text-xs text-amber-900 font-medium flex items-center justify-center gap-2 transition-all"
          >
            <span className="relative flex h-2 w-2">
              <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-amber-400 opacity-75"></span>
              <span className="relative inline-flex rounded-full h-2 w-2 bg-amber-600"></span>
            </span>
            <span>Reconectando tiempo real...</span>
          </div>
        )}
      <div
        ref={messagesContainerRef}
        onTouchStart={(e) => pullToRefresh.handlers.onTouchStart(e.nativeEvent)}
        onTouchMove={(e) => pullToRefresh.handlers.onTouchMove(e.nativeEvent)}
        onTouchEnd={() => pullToRefresh.handlers.onTouchEnd()}
        className="flex-1 overflow-y-auto px-2 sm:px-4 lg:px-8 py-3 sm:py-5 lg:py-6 min-h-0 relative"
        style={{
          backgroundColor: '#eae6df',
          backgroundImage: `radial-gradient(circle at top left, rgba(255,255,255,0.7), rgba(255,255,255,0) 30%), url("data:image/svg+xml,%3Csvg width='60' height='60' viewBox='0 0 60 60' xmlns='http://www.w3.org/2000/svg'%3E%3Cg fill='none' fill-rule='evenodd'%3E%3Cg fill='%23cfc9be' fill-opacity='0.25'%3E%3Cpath d='M36 34v-4h-2v4h-4v2h4v4h2v-4h4v-2h-4zm0-30V0h-2v4h-4v2h4v4h2V6h4V4h-4zM6 34v-4H4v4H0v2h4v4h2v-4h4v-2H6zM6 4V0H4v4H0v2h4v4h2V6h4V4H6z'/%3E%3C/g%3E%3C/g%3E%3C/svg%3E")`,
        }}
      >
        <PullToRefreshIndicator
          pullDistance={pullToRefresh.pullDistance}
          isRefreshing={pullToRefresh.isRefreshing}
          progress={pullToRefresh.getRefreshProgress()}
          shouldTrigger={pullToRefresh.shouldTrigger()}
        />
        {/* Banner de Caso Escalado por Agente IA */}
        {conversation.mode === 'human' && (botSummaryNote || (conversation as any).isEscalatedByBot || salesContext?.handoff) && (
          <motion.div
            role="alert"
            aria-label="Caso escalado por el Agente IA"
            initial={{ opacity: 0, y: -10, scale: 0.99 }}
            animate={{
              opacity: 1,
              y: 0,
              scale: 1,
              boxShadow: [
                '0 1px 3px 0 rgba(168, 85, 247, 0.1)',
                '0 4px 14px 2px rgba(168, 85, 247, 0.22)',
                '0 1px 3px 0 rgba(168, 85, 247, 0.1)',
              ],
              borderColor: [
                'rgba(233, 213, 255, 0.8)',
                'rgba(192, 132, 252, 0.9)',
                'rgba(233, 213, 255, 0.8)',
              ],
            }}
            transition={{
              boxShadow: { duration: 2.6, repeat: Infinity, ease: 'easeInOut' },
              borderColor: { duration: 2.6, repeat: Infinity, ease: 'easeInOut' },
              default: { duration: 0.25 },
            }}
            className="mx-auto mb-3 max-w-5xl rounded-2xl border bg-gradient-to-r from-purple-50 via-indigo-50/70 to-purple-50 p-3 sm:p-4 shadow-xs"
          >
            <div className="flex flex-wrap items-center justify-between gap-3">
              <div className="flex items-center gap-3 min-w-0">
                <div className="flex h-9 w-9 flex-shrink-0 items-center justify-center rounded-xl bg-purple-100 text-purple-700 shadow-2xs">
                  <Bot className="h-5 w-5" />
                </div>
                <div className="min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className="text-xs sm:text-sm font-bold text-purple-950 uppercase tracking-wide">
                      Caso escalado por el Agente IA
                    </span>
                    <span className="inline-flex items-center gap-1 rounded-full bg-purple-200/80 px-2 py-0.5 text-[10px] font-bold text-purple-800">
                      <motion.span
                        className="w-1.5 h-1.5 rounded-full bg-purple-600"
                        animate={{ opacity: [1, 0.2, 1] }}
                        transition={{ duration: 1.2, repeat: Infinity, ease: 'easeInOut' }}
                      />
                      Atención Humana Activa
                    </span>
                  </div>
                  <p className="text-[11px] sm:text-xs text-purple-900/80 mt-0.5">
                    El asistente IA transfirió esta conversación para atención personalizada. El bot permanecerá en pausa hasta que decidas reanudarlo.
                  </p>
                </div>
              </div>

              <div className="flex items-center gap-2 flex-shrink-0">
                {botSummaryNote && (
                  <button
                    type="button"
                    onClick={() => setActiveTab('notes')}
                    className="inline-flex items-center gap-1.5 rounded-lg border border-purple-200 bg-white px-3 py-1.5 text-xs font-semibold text-purple-800 shadow-2xs hover:bg-purple-50 active:scale-95 transition-all"
                  >
                    <span>Ver resumen IA</span>
                  </button>
                )}
                <button
                  type="button"
                  onClick={handleOpenResumeBotModal}
                  className="inline-flex items-center gap-1.5 rounded-lg bg-white border border-slate-200 px-3 py-1.5 text-xs font-semibold text-slate-700 shadow-2xs hover:bg-slate-50 active:scale-95 transition-all"
                >
                  <span>Reanudar Bot</span>
                </button>
              </div>
            </div>
          </motion.div>
        )}

        {/* Sales Funnel Progress & Commercial Handoff */}
        {salesContext && (
          <div className="mx-auto mb-4 max-w-5xl space-y-2.5">
            <FunnelStageStepper
              currentStage={salesContext.stage}
              checkoutReadiness={salesContext.checkoutReadiness}
              frustrationScore={salesContext.frustrationScore}
              dominantIntent={salesContext.dominantIntent}
            />

            {(salesContext.handoff || salesContext.activeOrderDraft?.items?.length) ? (
              <>
              <AppointmentRequestCard conversationId={id || ''} />
              <HandoffCommercialCard
                conversationId={id || ''}
                salesContext={salesContext}
                agentName={user?.name || 'del equipo de ventas'}
                businessType={tenant?.businessType}
                onCreateOrder={handleOpenOrder}
                onPrefillMessage={(text) => {
                  setComposerPrefillText(text)
                  setComposerPrefillVersion((v) => v + 1)
                }}
                botSummaryText={botSummaryText || undefined}
                onResumeBot={handleOpenResumeBotModal}
                onRequestClose={() => setIsCloseModalOpen(true)}
              />
              </>
            ) : (
              <>
                <AppointmentRequestCard conversationId={id || ''} />
                <ActiveOrderDraftCard
                  conversationId={id || ''}
                  salesContext={salesContext}
                  canViewAllConversations={canViewAllConversations}
                  onCreateOrder={handleOpenOrder}
                />
                {botSummaryNote && (
                  <BotSummaryCard
                    summary={botSummaryText}
                    createdAt={formatMessageTime(botSummaryNote.createdAt)}
                    onOpenNotes={() => setActiveTab('notes')}
                  />
                )}
              </>
            )}
          </div>
        )}

        {!salesContext && (
          <div className="mx-auto mb-4 max-w-5xl space-y-2.5">
            <AppointmentRequestCard conversationId={id || ''} />
            {botSummaryNote && (
              <BotSummaryCard
                summary={botSummaryText}
                createdAt={formatMessageTime(botSummaryNote.createdAt)}
                onOpenNotes={() => setActiveTab('notes')}
              />
            )}
          </div>
        )}
        {allMessages.length === 0 ? (
          <div className="flex flex-col items-center justify-center h-full space-y-3 sm:space-y-4 px-4">
            <div className="text-4xl sm:text-6xl">💬</div>
            <div className="text-center">
              <p className="text-gray-900 font-medium mb-1 text-sm sm:text-base">No hay mensajes</p>
              <p className="text-gray-500 text-xs sm:text-sm">
                Inicia la conversación enviando un mensaje
              </p>
            </div>
            {isBotTyping && (
              <div className="pt-2">
                <TypingIndicator
                  isBot={true}
                  userName="Bot respondiendo"
                />
              </div>
            )}
          </div>
        ) : (
          <div className="space-y-4 sm:space-y-5 lg:max-w-5xl lg:mx-auto">
            {hasHiddenPreviousMessages && (
              <div className="flex justify-center pt-1 pb-2">
                <button
                  type="button"
                  onClick={() => setVisibleMessagesCount((prev) => prev + 50)}
                  className="inline-flex items-center gap-1.5 rounded-full border border-slate-300 bg-white/95 px-3.5 py-1 text-xs font-semibold text-slate-700 shadow-2xs hover:bg-slate-50 active:scale-95 transition-all"
                >
                  <span>Cargar mensajes anteriores ({allMessages.length - visibleMessagesCount} restantes)</span>
                </button>
              </div>
            )}
            {messageGroups.map((group, groupIndex) => (
              <div key={groupIndex}>
                <div className="flex justify-center mb-3 sm:mb-4 sticky top-0 z-10 pointer-events-none">
                  <span className="pointer-events-auto bg-white/80 backdrop-blur-xs px-3 py-1 rounded-md text-[11px] text-slate-600 font-semibold shadow-2xs border border-slate-200/60 uppercase tracking-wider">
                    {group.date}
                  </span>
                </div>

                <div className="space-y-1 sm:space-y-1.5">
                  {group.messages.map((message, msgIndex) => {
                    const sessionMessage = message as any
                    const showSessionBreak = sessionBreakMessageIds.has(message.id)

                    if (message.isAiHandoffSummary) {
                      return (
                        <Fragment key={message.id}>
                          {showSessionBreak && (
                            <SessionDemarcationBar
                              kind={sessionMessage.sessionKind}
                              closedAt={sessionMessage.sessionClosedAt}
                              closeReason={sessionMessage.sessionCloseReason}
                              startedAt={
                                sessionMessage.sessionKind === 'current'
                                  ? String(conversation.createdAt ?? '')
                                  : undefined
                              }
                            />
                          )}
                          <BotSummaryHandoffMessage
                            message={message}
                            salesContext={salesContext}
                            formatTime={formatMessageTime}
                            onCreateOrder={handleOpenOrder}
                            onPrefillMessage={(text) => {
                              setComposerPrefillText(text)
                              setComposerPrefillVersion((v) => v + 1)
                            }}
                            agentName={user?.name || 'del equipo de ventas'}
                            businessType={tenant?.businessType}
                          />
                        </Fragment>
                      )
                    }

                    const repliedMessage = message.replyToMessageId
                      ? conversation.messages.find((m) => m.messageId === message.replyToMessageId)
                      : undefined
                    const isFirstInGroup = isFirstInMessageGroup(
                      msgIndex > 0 ? group.messages[msgIndex - 1] : undefined,
                      message
                    )

                    return (
                      <Fragment key={message.id}>
                        {showSessionBreak && (
                          <SessionDemarcationBar
                            kind={sessionMessage.sessionKind}
                            closedAt={sessionMessage.sessionClosedAt}
                            closeReason={sessionMessage.sessionCloseReason}
                            startedAt={
                              sessionMessage.sessionKind === 'current'
                                ? String(conversation.createdAt ?? '')
                                : undefined
                            }
                          />
                        )}
                        <MessageBubble
                          message={message}
                          repliedMessage={repliedMessage}
                          direction={message.direction}
                          isFirstInGroup={isFirstInGroup}
                          onReply={() => setReplyingTo(message)}
                          onDelete={handleDeleteMessage}
                          onCancelScheduled={handleCancelScheduledMessage}
                          onReaction={handleAddReaction}
                          onImageClick={(url, alt) => setLightboxImage({ url, alt })}
                          formatTime={formatMessageTime}
                          mediaLoadState={mediaLoadState[message.id]}
                          onMediaLoad={() => handleMediaLoad(message.id)}
                          onMediaError={() => handleMediaError(message.id)}
                        />
                      </Fragment>
                    )
                  })}
                </div>
              </div>
            ))}
            {isBotTyping && (
              <TypingIndicator
                isBot={true}
                userName="Bot respondiendo"
              />
            )}

            {otherUserTyping && (
              <TypingIndicator
                userName={otherUserTyping}
                avatarUrl={conversation.avatarUrl}
                avatarColor={getAvatarColor(conversation.waContact)}
                avatarInitials={getContactAvatar(conversation.waContact, conversation.contactName)}
              />
            )}

            <div ref={messagesEndRef} />
          </div>
        )}

        <ScrollToBottom
          show={showScrollButton}
          unreadCount={newMessagesBelow}
          onClick={() => scrollToBottom('smooth')}
        />
      </div>
      </>
      )}

      {sendError && (
        <div
          role="alert"
          data-testid="send-error-banner"
          className="bg-red-50 border-t border-red-200 px-4 lg:px-6 py-2.5"
        >
          <div className="flex items-center gap-3 lg:max-w-5xl lg:mx-auto">
            <AlertCircle className="h-4 w-4 text-red-600 flex-shrink-0" />
            <p className="flex-1 text-xs lg:text-sm text-red-800">
              No se pudo enviar tu mensaje. {sendError.message}
            </p>
            <button
              type="button"
              onClick={() => {
                const payload = sendError.payload
                setSendError(null)
                sendMessageMutation.mutate(payload)
              }}
              disabled={sendMessageMutation.isPending}
              className="rounded-md bg-red-600 px-3 py-1.5 text-xs font-medium text-white hover:bg-red-700 disabled:opacity-50"
            >
              Reintentar
            </button>
            <button
              type="button"
              onClick={() => setSendError(null)}
              aria-label="Descartar error de envío"
              className="p-1 text-red-600 hover:text-red-800"
            >
              <CloseIcon className="h-4 w-4" />
            </button>
          </div>
        </div>
      )}

      <SessionStatusBanner
        error={sessionError}
        status={sessionStatus}
        isReconnecting={isReconnectingSession}
        onReconnect={handleReconnectSession}
        onDismiss={() => setSessionError(null)}
      />

      {activeTab !== 'notes' && (
        <div className="lg:px-6 lg:py-4 lg:border-t lg:bg-gray-50">
          <div className="lg:max-w-5xl lg:mx-auto">
            <StaffConsultationPanel conversationId={id} enabled={conversation.status !== 'closed'} />

            <HitlResolutionCard
              pending={hitlPending}
              reasonBadge={hitlReasonBadge}
              reasonLabel={hitlReasonFriendlyLabel}
              previewText={hitlPendingPreviewFromTrace}
              feedback={hitlFeedback}
              error={hitlError}
              onOpenModal={() => setShowHitlModal(true)}
            />

            {conversation.status === 'closed' ? (
              <>
                {conversation.activeConversationId && (
                  <ActiveSessionBanner
                    activeConversationId={conversation.activeConversationId}
                    onGoToActive={(activeId) => navigate(`/app/conversations/${activeId}`)}
                  />
                )}
                <ClosedConversationBar
                  isReopening={statusMutation.isPending}
                  onReopen={() => statusMutation.mutate('open')}
                  closeReason={conversation.closeReason}
                  closeNotes={conversation.closeNotes}
                  closedAt={conversation.closedAt}
                />
              </>
            ) : isTeamQueueChat ? (
              <TeamQueueTakeoverBar
                isClaiming={claimMutation.isPending}
                onClaim={() => id && claimMutation.mutate(id)}
                onAddNote={() => setActiveTab('notes')}
              />
            ) : conversation.mode === 'bot' ? (
              <BotTakeoverBar
                isEscalating={escalateMutation.isPending}
                onTakeover={() => escalateMutation.mutate('human')}
                onAddNote={() => setActiveTab('notes')}
                salesStageLabel={
                  salesContext?.stage
                    ? getSalesStageBadge(salesContext.stage).label
                    : undefined
                }
              />
            ) : (
              <>
                {hitlDraftCandidate && dismissedSuggestedDraft !== hitlDraftCandidate && (
                  <InlineSuggestedResponse
                    text={hitlDraftCandidate}
                    onApply={(text) => loadPendingResponseAsDraft(text)}
                    onDismiss={() => setDismissedSuggestedDraft(hitlDraftCandidate)}
                    title="Respuesta sugerida (Pausa HITL)"
                  />
                )}
                <MessageComposer
                  onSendText={handleSendText}
                  onSendImage={handleSendImage}
                  onSendFile={handleSendFile}
                  disabled={sendMessageMutation.isPending}
                  placeholder="Escribe un mensaje"
                  prefillText={composerPrefillText}
                  prefillVersion={composerPrefillVersion}
                  focusTrigger={composerFocusTrigger}
                  cannedVars={cannedVarValues}
                  onTyping={(typing) => {
                    if (id) {
                      conversationSocket.emitTyping(id, typing)
                    }
                  }}
                />
              </>
            )}
          </div>
        </div>
      )}

      <Modal
        isOpen={showHitlModal}
        onClose={() => setShowHitlModal(false)}
        title="Revisar pausa HITL"
        size="md"
      >
        <div className="space-y-4">
          <p className="text-sm leading-6 text-gray-700">
            Revisa la sugerencia, confirma la accion humana y reanuda el flujo de checkout/pago. Si la decision queda aprobada, intentaremos enviar el mensaje al cliente automaticamente.
          </p>

          <div className="rounded-lg border border-orange-200 bg-orange-50 px-3 py-3">
            <div className="mb-2 flex items-center justify-between gap-2">
              <div className="text-xs font-semibold uppercase tracking-wide text-orange-800">Motivo detectado</div>
              {hitlReasonBadge}
            </div>
            <div className="text-xs text-orange-700">
              {hitlReasonFriendlyLabel}
              <span className="mx-1 text-orange-500">•</span>
              {hitlReasonDisplay}
            </div>
          </div>

          {hitlDraftCandidate && (
            <div className="space-y-2 rounded-lg border border-emerald-200 bg-emerald-50 px-3 py-3">
              <div className="text-xs font-semibold uppercase tracking-wide text-emerald-800">Borrador sugerido</div>
              <p className="text-xs leading-6 text-emerald-900 whitespace-pre-wrap break-words">
                {hitlDraftCandidate}
              </p>
              <button
                type="button"
                onClick={() => loadPendingResponseAsDraft(hitlDraftCandidate)}
                className="inline-flex items-center rounded-md border border-emerald-300 bg-white px-3 py-2 text-xs font-medium text-emerald-700 hover:bg-emerald-100"
              >
                Cargar en el composer
              </button>
            </div>
          )}

          <div className="space-y-1.5">
            <label className="text-xs font-medium text-gray-700">Decision</label>
            <select
              value={hitlAction}
              onChange={(event) => setHitlAction(event.target.value as 'approve' | 'edit' | 'deny')}
              className="w-full rounded-md border border-gray-300 bg-white px-3 py-2.5 text-sm text-gray-800"
            >
              <option value="approve">Aprobar</option>
              <option value="edit">Editar</option>
              <option value="deny">Rechazar</option>
            </select>
          </div>

          <div className="space-y-1.5">
            <label className="text-xs font-medium text-gray-700">
              {hitlAction === 'edit' ? 'Mensaje final para cliente' : 'Nota interna (opcional)'}
            </label>
            <textarea
              rows={4}
              value={hitlAction === 'edit' ? hitlMessage : hitlReason}
              onChange={(event) => {
                if (hitlAction === 'edit') {
                  setHitlMessage(event.target.value)
                } else {
                  setHitlReason(event.target.value)
                }
              }}
              placeholder={
                hitlAction === 'edit'
                  ? 'Escribe aqui la version final para el cliente'
                  : 'Agrega una nota breve para el equipo'
              }
              className="w-full resize-y rounded-md border border-gray-300 bg-white px-3 py-2.5 text-sm text-gray-800"
            />
          </div>

          {hitlError && <p className="text-xs text-red-600">{hitlError}</p>}

          <div className="flex justify-end gap-2 pt-1">
            <button
              type="button"
              onClick={() => setShowHitlModal(false)}
              className="rounded-md border border-gray-300 px-3 py-2 text-sm text-gray-700 hover:bg-gray-50"
            >
              Cancelar
            </button>
            <button
              type="button"
              onClick={handleResumeHitl}
              disabled={hitlResumeMutation.isPending}
              className="rounded-md bg-orange-600 px-3 py-2 text-sm font-medium text-white hover:bg-orange-700 disabled:cursor-not-allowed disabled:opacity-50"
            >
              {hitlResumeMutation.isPending ? 'Aplicando...' : 'Aplicar decision'}
            </button>
          </div>
        </div>
      </Modal>

      {/* Modal Reanudar Bot con Contexto HITL */}
      <Modal
        isOpen={showResumeBotModal}
        onClose={() => setShowResumeBotModal(false)}
        title="Reanudar Bot de Ventas"
        size="md"
      >
        <div className="space-y-4">
          <p className="text-sm leading-6 text-gray-700">
            El bot retomará la atención automatizada del cliente en su próximo mensaje. Puedes agregar una nota comercial o acuerdo alcanzado para que el bot la tome en cuenta de inmediato.
          </p>

          <div className="space-y-1.5">
            <label htmlFor="resume-bot-note" className="block text-xs font-semibold text-gray-700">
              Acuerdo comercial o nota para el bot (opcional)
            </label>
            <textarea
              id="resume-bot-note"
              rows={3}
              value={resumeBotNote}
              onChange={(e) => setResumeBotNote(e.target.value)}
              placeholder="Ej: Precio especial acordado en $45, o cliente confirmó repuesto para modelo 2011..."
              className="w-full rounded-lg border border-gray-300 p-2.5 text-xs text-gray-900 placeholder-gray-400 focus:border-emerald-500 focus:ring-1 focus:ring-emerald-500"
            />
            <p className="text-[11px] text-gray-500">
              Si dejas este campo vacío, el bot continuará normalmente con el contexto previo limpio de objeciones.
            </p>
          </div>

          <div className="flex items-center justify-end gap-2 pt-2 border-t border-gray-100">
            <button
              type="button"
              onClick={() => setShowResumeBotModal(false)}
              disabled={escalateMutation.isPending}
              className="rounded-md border border-gray-300 px-3 py-2 text-xs font-medium text-gray-700 hover:bg-gray-50"
            >
              Cancelar
            </button>
            <button
              type="button"
              onClick={handleConfirmResumeBot}
              disabled={escalateMutation.isPending}
              className="inline-flex items-center gap-1.5 rounded-md bg-emerald-600 px-3 py-2 text-xs font-medium text-white hover:bg-emerald-700 disabled:opacity-50"
            >
              <Bot className="h-4 w-4" />
              <span>{escalateMutation.isPending ? 'Reanudando...' : 'Reanudar Bot Ahora'}</span>
            </button>
          </div>
        </div>
      </Modal>

      {lightboxImage && (
        <div
          className="fixed inset-0 z-40 bg-black/80 lg:bg-black/90 flex items-center justify-center px-4 lg:px-8"
          onClick={() => setLightboxImage(null)}
        >
          <div
            className="relative max-w-[90vw] max-h-[90vh] lg:max-w-[80vw] lg:max-h-[85vh]"
            onClick={(e) => e.stopPropagation()}
          >
            <button
              type="button"
              onClick={() => setLightboxImage(null)}
              className="absolute -top-3 -right-3 lg:-top-4 lg:-right-4 bg-black/70 hover:bg-black text-white rounded-full p-1.5 lg:p-2 focus:outline-none transition-colors"
            >
              <CloseIcon className="h-4 w-4 lg:h-5 lg:w-5" />
            </button>
            <img
              src={lightboxImage.url}
              alt={lightboxImage.alt || 'Imagen'}
              className="max-w-full max-h-[90vh] lg:max-h-[85vh] object-contain rounded-lg shadow-2xl bg-black"
            />
          </div>
        </div>
      )}

      {/* Contact Panel */}
      {conversation && (
        <ContactPanel
          contactId={conversation.waContact}
          detectedName={
            conversation.salesContext?.handoff?.customerName ||
            conversation.contactName ||
            undefined
          }
          isOpen={showContactPanel}
          onClose={() => setShowContactPanel(false)}
        />
      )}

      {/* Modal de Cierre de Conversación con Motivo */}
      <CloseConversationModal
        isOpen={isCloseModalOpen}
        onClose={() => setIsCloseModalOpen(false)}
        onConfirm={({ closeReason, closeNotes }) => {
          statusMutation.mutate({
            status: 'closed',
            closeReason,
            closeNotes,
          })
        }}
        isLoading={statusMutation.isPending}
        contactName={conversation?.contactName || conversation?.waContact}
        defaultIsWon={Boolean(
          salesContext?.stage === 'closing' ||
          salesContext?.stage === 'post_sale' ||
          botSummaryText?.includes('🎉')
        )}
      />

      {/* Modal Confirmación de Vaciar Mensajes */}
      <Modal
        isOpen={isClearModalOpen}
        onClose={() => !clearMessagesMutation.isPending && setIsClearModalOpen(false)}
        title="¿Vaciar mensajes de la conversación?"
        size="sm"
      >
        <div className="space-y-4">
          <p className="text-sm text-gray-600">
            Esta acción eliminará de forma permanente todo el historial de mensajes de este chat y lo dejará en blanco. El estado y la sesión del bot se reiniciarán.
          </p>
          <div className="p-3 bg-amber-50 border border-amber-200 rounded-lg text-xs text-amber-800">
            ⚠️ <strong>Esta acción no se puede deshacer.</strong>
          </div>
          <div className="flex justify-end gap-2 pt-2 border-t">
            <button
              type="button"
              onClick={() => setIsClearModalOpen(false)}
              disabled={clearMessagesMutation.isPending}
              className="px-4 py-2 text-sm text-gray-700 bg-gray-100 hover:bg-gray-200 rounded-lg disabled:opacity-50 transition-colors"
            >
              Cancelar
            </button>
            <button
              type="button"
              onClick={() => clearMessagesMutation.mutate()}
              disabled={clearMessagesMutation.isPending}
              className="flex items-center gap-1.5 px-4 py-2 text-sm font-medium text-white bg-rose-600 hover:bg-rose-700 rounded-lg disabled:opacity-50 transition-colors"
            >
              {clearMessagesMutation.isPending ? (
                <>
                  <Loader2 className="h-4 w-4 animate-spin" />
                  <span>Vaciando...</span>
                </>
              ) : (
                <>
                  <Trash2 className="h-4 w-4" />
                  <span>Vaciar mensajes</span>
                </>
              )}
            </button>
          </div>
        </div>
      </Modal>
    </div>
  )
}

